<script setup lang="ts">
import SimpleTable from '@/components/tables/SimpleTable.vue';
import { RouteName } from '@/router/models/route-name';
import router from '@/router/router';
import { overlaySpinnerService } from '@/services/app/overlay-spinner-service';
import { ataService } from '@/services/ata/ata-service';
import { notificationService } from '@/services/notification/notification-service';
import type { BaseColumnData } from '@/utils/tables/column-data';
import { translateKey } from '@/utils/translation-utils';
import { PhPencilSimple } from '@phosphor-icons/vue';
import { models, type Nullish, utils } from 'cesg-shared';
import { nextTick, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

interface PermissionTo {
  edit?: {
    ata?: boolean;
  };
}

interface Props {
  hasPermissionTo?: PermissionTo;
}

const props = withDefaults(defineProps<Props>(), {
  hasPermissionTo: () => ({
    edit: {
      ata: true,
    },
  }),
});

const { t } = useI18n();
const data = ref<models.ListAtaResponse[]>([]);
const amountKey = 'totalCost';
const editState = ref<{ rowId: string | null; columnKey: string | null }>({
  rowId: null,
  columnKey: null,
});
const editInputRef = ref<HTMLInputElement | null>(null);
const beforeEditValue = ref<Nullish<string | number>>(null);
const loading = ref<boolean>(false);

const route = useRoute();

const columns: BaseColumnData[] = [
  { key: 'serialNumber', label: t('ata.table.serialNumber'), sortable: true },
  { key: 'title', label: t('ata.table.title'), sortable: true },
  {
    key: 'status',
    label: t('ata.table.status'),
    format: translateKey,
    sortable: true,
  },
  { key: 'type', label: t('ata.table.type'), sortable: true },
  { key: 'pricingModel', label: t('ata.table.pricingModel'), sortable: true },
  { key: 'priority', label: t('ata.table.priority'), sortable: true },
  {
    key: amountKey,
    label: t('ata.table.totalCost'),
    format: utils.formatNumber,
    align: 'has-text-right',
    sortable: true,
  },
  {
    key: 'sentDate',
    label: t('ata.table.sentDate'),
    align: 'has-text-right',
    sortable: true,
  },
  {
    key: 'deadlineDate',
    label: t('ata.table.deadlineDate'),
    align: 'has-text-right',
    sortable: true,
  },
  {
    key: 'answerReceivedDate',
    label: t('ata.table.answerReceivedDate'),
    align: 'has-text-right',
    sortable: true,
  },
  {
    key: 'revisionDate',
    label: t('ata.table.revisionDate'),
    align: 'has-text-right',
    sortable: true,
  },
  {
    key: 'invoiced',
    label: t('ata.table.invoiced'),
    align: 'has-text-right',
    editable: () => true,
    editableInputType: 'string',
    sortable: true,
  },
];

const loadData = async () => {
  overlaySpinnerService.show();
  try {
    data.value = await ataService.getAll(
      utils.type.asType<string>(route.params.id),
    );
  } finally {
    overlaySpinnerService.hide();
  }
};

const enableEdit = async (rowId: string, columnKey: string) => {
  editState.value = { rowId, columnKey };
  await nextTick(() => {
    const input = document.querySelector(
      `#input-${rowId}-${columnKey}`,
    ) as HTMLInputElement | null;
    if (input) {
      editInputRef.value = input;
      input.focus();
    }
  });
};

const save = async (row: models.ListAtaResponse, column: string) => {
  loading.value = true;
  try {
    const updatedRow = await ataService.updateAtaInvoiceComment(row.id, {
      invoiced: row.invoiced,
    });
    notificationService.open({
      text: t('notifications.saveSuccess'),
      type: 'success',
    });
    Object.assign(row, updatedRow);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    (row[column as keyof typeof row] as typeof beforeEditValue.value) =
      beforeEditValue.value;
    beforeEditValue.value = null;
  } finally {
    editState.value = { rowId: null, columnKey: null };
    loading.value = false;
  }
};

function saveCurrentValue(value: Nullish<string | number>) {
  beforeEditValue.value = value;
}

const navigateToOverview = async (id: string) => {
  await router.push({
    name: RouteName.ATADetails,
    params: { ataId: id, id: route.params.id },
  });
};

onMounted(loadData);
</script>

<template>
  <SimpleTable
    :columns="columns"
    :data="data"
    :sum-column-keys="[amountKey]"
    :enable-search="true"
  >
    <template #tbody="{ data }">
      <tr
        v-for="(row, rowIndex) in data"
        :key="row.id"
        @click="navigateToOverview(row.id)"
      >
        <td
          v-for="(column, index) in columns"
          :key="index"
          :class="[
            column.align || '',
            column.class ? column.class(row[column.key]) : '',
          ]"
        >
          <span v-if="column.key === 'serialNumber'">
            {{ t('ata.nameSerialNr', { serialNumber: row[column.key] }) }}
          </span>
          <template
            v-else-if="
              editState.rowId === row.id && editState.columnKey === column.key
            "
          >
            <div class="control is-small" :class="{ 'is-loading': loading }">
              <input
                v-model.lazy="data[rowIndex][column.key]"
                :id="`input-${row.id}-${column.key}`"
                @blur="save(row, column.key)"
                @keyup.enter="($event.target as HTMLInputElement).blur()"
                @focus.prevent="saveCurrentValue(data[rowIndex][column.key])"
                :type="column.editableInputType"
                :step="column.editableInputType === 'number' ? '0.01' : ''"
                class="input is-small"
                ref="editInputRef"
              />
            </div>
          </template>
          <template v-else>
            <span
              :class="{
                editable: column.editable,
                tag: column.key === 'status',
                [row[column.key]]: true,
              }"
            >
              {{
                column.format ? column.format(row[column.key]) : row[column.key]
              }}
              <button
                v-if="column.editable && props.hasPermissionTo?.edit?.ata"
                class="button is-white"
                @click.stop="enableEdit(row.id, column.key)"
              >
                <PhPencilSimple class="icon" weight="duotone" />
              </button>
            </span>
          </template>
        </td>
      </tr>
    </template>
  </SimpleTable>
</template>

<style lang="scss" scoped>
@use '@/assets/style/variables';
tr {
  background-color: var(--table-light);
}
.tag {
  &.inprogress {
    background-color: var(--ata-inprogress);
  }
  &.done {
    background-color: var(--ata-done);
  }
  &.onreferral {
    background-color: var(--ata-onreferral);
  }
  &.rejected {
    background-color: var(--ata-rejected);
  }
  &.underdevelopment {
    background-color: var(--ata-underdevelopment);
  }
  &.invoicingok {
    background-color: var(--ata-invoicingok);
  }
  &.approved {
    background-color: var(--ata-approved);
  }
}
</style>
